import { deleteCookie, getCookie, setCookie } from 'cookies-next';

import { ACCESS_TOKEN, KEEP_ME_LOGGED_IN, REFRESH_TOKEN } from './constants';

// cookies-next does not re-export its OptionsType, so derive it from the API.
export type CookieContext = Parameters<typeof getCookie>[1];

// Keep in sync with the refresh token's `expiresIn: '7d'` in
// apps/api/src/auth/auth.service.ts (getTokens).
export const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

/**
 * `Secure` is gated on the deployed environment because a `Secure` cookie is
 * silently dropped by the browser over plain `http://localhost`, which would
 * break local development entirely.
 *
 * NEXT_PUBLIC_APP_ENV is the deployed discriminator (set in Vercel:
 * local/staging/production) — NODE_ENV is 'production' on Vercel staging too,
 * so it must not be the primary source. This mirrors the resolution order
 * already used in src/instrumentation-client.ts.
 *
 * Note NEXT_PUBLIC_* is inlined at build time, so this is decided by the build
 * environment, not by runtime config.
 */
export const isSecureEnvironment = (): boolean => {
  const appEnv =
    process.env.NEXT_PUBLIC_APP_ENV || process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;

  if (appEnv === 'local' || appEnv === 'development' || appEnv === 'test') return false;

  return appEnv === 'staging' || appEnv === 'production';
};

/**
 * Browser-enforced protections applied to every auth cookie this app writes.
 *
 * - `secure`   — the browser refuses to attach the cookie to a plaintext
 *                `http://` request, so a typo'd link or a network attacker
 *                forcing one navigation can no longer leak the session
 *                (GitHub #2076).
 * - `sameSite` — 'lax' is set explicitly rather than left to the browser
 *                default so the attribute is pinned to a known value instead
 *                of drifting with browser defaults. It must stay 'lax' (not
 *                'strict'): the SSO and Shopify handoff flows return to the
 *                app via a cross-site top-level navigation, and 'strict' would
 *                withhold the cookie on that first request and bounce the user
 *                back to the login page.
 * - `path`     — pinned to '/' to match the cookies already in the wild, so
 *                writes after this change overwrite existing sessions rather
 *                than creating a second, shadowing cookie at a narrower path.
 *
 * `httpOnly` is deliberately NOT set here and cannot be: these cookies are
 * written by client-side JavaScript, and the browser ignores `HttpOnly` on
 * any cookie set via `document.cookie`. Closing #2075 requires moving the
 * write server-side — see the follow-up plan on that issue.
 */
export const getAuthCookieOptions = () => ({
  secure: isSecureEnvironment(),
  sameSite: 'lax' as const,
  path: '/',
});

/**
 * Record the user's "Keep me signed in" choice. When enabled, auth cookies
 * are written with a persistent max-age so the session survives browser
 * restarts; otherwise they remain session cookies (cleared on close).
 */
export const setKeepMeLoggedIn = (enabled: boolean) => {
  if (enabled) {
    setCookie(KEEP_ME_LOGGED_IN, 'true', {
      ...getAuthCookieOptions(),
      maxAge: AUTH_COOKIE_MAX_AGE,
    });
  } else {
    deleteCookie(KEEP_ME_LOGGED_IN, getAuthCookieOptions());
  }
};

/**
 * Store the JWT pair, persisting across browser restarts when the user
 * opted into "Keep me signed in".
 *
 * Pass the getServerSideProps context when calling during SSR so the
 * keep-me-logged-in cookie can be read from the request and the refreshed
 * tokens reach the browser via Set-Cookie on the response.
 */
export const setAuthCookies = (
  accessToken: string,
  refreshToken: string,
  context?: CookieContext
) => {
  const persist = !!getCookie(KEEP_ME_LOGGED_IN, context);
  const options = {
    ...context,
    ...getAuthCookieOptions(),
    ...(persist ? { maxAge: AUTH_COOKIE_MAX_AGE } : {}),
  };

  setCookie(ACCESS_TOKEN, accessToken, options);
  setCookie(REFRESH_TOKEN, refreshToken, options);
};

/**
 * Clear every auth cookie.
 *
 * A cookie is only overwritten when the delete carries the same `path` (and
 * domain) as the write, so logout MUST reuse getAuthCookieOptions() — deleting
 * with mismatched attributes leaves the original cookie in place and the user
 * stays signed in. Callers that also clear non-auth cookies (e.g. the
 * conversation id) should keep doing so separately.
 */
export const clearAuthCookies = (context?: CookieContext) => {
  const options = { ...context, ...getAuthCookieOptions() };

  deleteCookie(ACCESS_TOKEN, options);
  deleteCookie(REFRESH_TOKEN, options);
  deleteCookie(KEEP_ME_LOGGED_IN, options);
};
